import {
  CoreMetricsData,
  DirectionalAgreementData,
  DirectionalAnalyticsRow,
  MarketStatsFilterOptions,
  MarketStatsFilterParams,
  OpenOrdersData,
  PayoutTickerItem,
  PinnedAssetDetail,
  PositionsMapData,
  RRRealizationData,
  WinLossDurationData,
} from '@monorepo/community-stats-common';
import BaseEntityService, { BaseResponse } from '../core/base-entity-service';

const buildFilterQuery = (
  filters?: MarketStatsFilterParams,
  extra?: Record<string, string | undefined>,
): string => {
  const params = new URLSearchParams();
  if (filters?.accountType && filters.accountType !== 'all') {
    params.set('accountType', filters.accountType);
  }
  if (filters?.programId) params.set('programId', filters.programId);
  if (filters?.country) params.set('country', filters.country);
  if (extra) {
    Object.entries(extra).forEach(([key, value]) => {
      if (value) params.set(key, value);
    });
  }
  const qs = params.toString();
  return qs ? `?${qs}` : '';
};

export default class CommunityStatsService extends BaseEntityService {
  static override route = 'v1/market-stats';

  async getPayoutTicker(): Promise<PayoutTickerItem[]> {
    try {
      const response = await this.httpService.get<
        BaseResponse<PayoutTickerItem[]>
      >(`${this.path}/payout-ticker`);
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch payout ticker', e);
      return [];
    }
  }

  async getFilters(): Promise<MarketStatsFilterOptions | null> {
    try {
      const response = await this.httpService.get<
        BaseResponse<MarketStatsFilterOptions>
      >(`${this.path}/filters`);
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch filters', e);
      return null;
    }
  }

  async getDurationAnalysis(
    symbol?: string,
    filters?: MarketStatsFilterParams,
  ): Promise<WinLossDurationData[]> {
    try {
      const qs = buildFilterQuery(filters, { symbol });
      const response = await this.httpService.get<
        BaseResponse<WinLossDurationData[]>
      >(`${this.path}/duration-analysis${qs}`);
      return response.data;
    } catch (e) {
      console.error(
        '[CommunityStatsService] Failed to fetch duration analysis',
        e,
      );
      return [];
    }
  }

  async getRRRealization(
    symbol?: string,
    filters?: MarketStatsFilterParams,
  ): Promise<RRRealizationData[]> {
    try {
      const qs = buildFilterQuery(filters, { symbol });
      const response = await this.httpService.get<
        BaseResponse<RRRealizationData[]>
      >(`${this.path}/rr-realization${qs}`);
      return response.data;
    } catch (e) {
      console.error(
        '[CommunityStatsService] Failed to fetch RR realization',
        e,
      );
      return [];
    }
  }

  async getCoreMetrics(
    filters?: MarketStatsFilterParams,
  ): Promise<CoreMetricsData | null> {
    try {
      const qs = buildFilterQuery(filters);
      const response = await this.httpService.get<
        BaseResponse<CoreMetricsData>
      >(`${this.path}/core-metrics${qs}`);
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch core metrics', e);
      return null;
    }
  }

  async getDirectionalAnalytics(
    filters?: MarketStatsFilterParams,
    options?: { limit?: number; search?: string },
  ): Promise<DirectionalAnalyticsRow[]> {
    try {
      const qs = buildFilterQuery(filters, {
        limit: options?.limit?.toString(),
        search: options?.search,
      });
      const response = await this.httpService.get<
        BaseResponse<DirectionalAnalyticsRow[]>
      >(`${this.path}/directional-analytics${qs}`);
      return response.data;
    } catch (e) {
      console.error(
        '[CommunityStatsService] Failed to fetch directional analytics',
        e,
      );
      return [];
    }
  }

  async getAgreementGauge(
    symbol?: string,
    filters?: MarketStatsFilterParams,
  ): Promise<DirectionalAgreementData[]> {
    try {
      const qs = buildFilterQuery(filters, { symbol });
      const response = await this.httpService.get<
        BaseResponse<DirectionalAgreementData[]>
      >(`${this.path}/agreement-gauge${qs}`);
      return response.data;
    } catch (e) {
      console.error(
        '[CommunityStatsService] Failed to fetch agreement gauge',
        e,
      );
      return [];
    }
  }

  async getAssetDetail(
    symbol: string,
    filters?: MarketStatsFilterParams,
  ): Promise<PinnedAssetDetail | null> {
    try {
      const qs = buildFilterQuery(filters);
      const response = await this.httpService.get<
        BaseResponse<PinnedAssetDetail>
      >(`${this.path}/asset/${encodeURIComponent(symbol)}${qs}`);
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch asset detail', e);
      return null;
    }
  }

  async getPinnedAssets(): Promise<string[]> {
    try {
      const response = await this.httpService.get<BaseResponse<string[]>>(
        `${this.path}/pinned-assets`,
      );
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch pinned assets', e);
      return [];
    }
  }

  async pinAsset(symbol: string): Promise<void> {
    try {
      await this.httpService.post(
        `${this.path}/pin/${encodeURIComponent(symbol)}`,
        {},
      );
    } catch (e) {
      console.error('[CommunityStatsService] Failed to pin asset', e);
    }
  }

  async getOpenOrders(
    symbol: string,
    filters?: MarketStatsFilterParams,
  ): Promise<OpenOrdersData | null> {
    try {
      const qs = buildFilterQuery(filters);
      const response = await this.httpService.get<BaseResponse<OpenOrdersData>>(
        `${this.path}/open-orders/${encodeURIComponent(symbol)}${qs}`,
      );
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch open orders', e);
      return null;
    }
  }

  async getPositionsMap(
    symbol: string,
    filters?: MarketStatsFilterParams,
  ): Promise<PositionsMapData | null> {
    try {
      const qs = buildFilterQuery(filters);
      const response = await this.httpService.get<
        BaseResponse<PositionsMapData>
      >(`${this.path}/positions-map/${encodeURIComponent(symbol)}${qs}`);
      return response.data;
    } catch (e) {
      console.error('[CommunityStatsService] Failed to fetch positions map', e);
      return null;
    }
  }

  async unpinAsset(symbol: string): Promise<void> {
    try {
      await this.httpService.delete(
        `${this.path}/unpin/${encodeURIComponent(symbol)}`,
      );
    } catch (e) {
      console.error('[CommunityStatsService] Failed to unpin asset', e);
    }
  }
}
